import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';
import { userChangeIsLoggedIn } from '~/redux/features/userSlide';
import { accountNavLinks } from '~/components/elements/AccountSidebar';

const MOBILE_PANEL_EVENT = 'mobile-panel-change';
const COLOR_RED_DARK = '#bf0000';
const COLOR_RED_DARK_BORDER = '#980000';
const NAV_ICON_SIZE = 20;

const BellIcon = ({ size = NAV_ICON_SIZE }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" width={size} height={size}>
    <path
      d="M12 3.5c-3 0-5 2.2-5 5.2v3.1c0 .6-.2 1.2-.6 1.7l-.9 1.1c-.6.8 0 1.9 1 1.9h11c1 0 1.6-1.1 1-1.9l-.9-1.1c-.4-.5-.6-1.1-.6-1.7V8.7c0-3-2-5.2-5-5.2Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.7 19.5a2.3 2.3 0 0 0 4.6 0"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WalletIcon = ({ size = NAV_ICON_SIZE }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" width={size} height={size}>
    <path
      d="M4 7.5A2.5 2.5 0 0 1 6.5 5H18a1 1 0 0 1 1 1v2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 7.5V17a2 2 0 0 0 2 2h12.5a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1H6.5A2.5 2.5 0 0 1 4 7.5Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="15.5" cy="13.5" r="1.1" fill="currentColor" />
  </svg>
);

const StarIcon = ({ size = NAV_ICON_SIZE }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" width={size} height={size}>
    <path
      d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9 6.8 19.6l1-5.8L3.5 9.7l5.9-.9L12 3.5Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

const AccountLinkIcon = ({ icon }) => {
  const iconStyle = {
    fontSize: `${NAV_ICON_SIZE}px`,
    width: `${NAV_ICON_SIZE}px`,
    height: `${NAV_ICON_SIZE}px`,
    lineHeight: '1',
    marginRight: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };
  if (icon === 'icon-bell') {
    return (
      <i className={icon} style={iconStyle}>
        <BellIcon />
      </i>
    );
  }
  if (icon === 'icon-wallet') {
    return (
      <i className={icon} style={iconStyle}>
        <WalletIcon />
      </i>
    );
  }
  if (icon === 'icon-star') {
    return (
      <i className={icon} style={iconStyle}>
        <StarIcon />
      </i>
    );
  }
  return <i className={icon} style={iconStyle} />;
};

const AccountQuickLinksMobile = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const isLoggedIn = useSelector(({ user }) => user.isLoggedIn);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [userData, setUserData] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('jwt');
    if (token) {
      setIsTokenValid(true);
      dispatch(userChangeIsLoggedIn(true));
      fetchUserData(token);
    } else {
      setIsTokenValid(false);
      dispatch(userChangeIsLoggedIn(false));
    }
  }, [dispatch]);

  useEffect(() => {
    if (panelOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [panelOpen]);

  useEffect(() => {
    const handleExternalPanelChange = (event) => {
      const activePanel = event?.detail?.panel;
      if (activePanel !== 'account') {
        setPanelOpen(false);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener(MOBILE_PANEL_EVENT, handleExternalPanelChange);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener(MOBILE_PANEL_EVENT, handleExternalPanelChange);
      }
    };
  }, []);

  const emitPanelChange = useCallback((panel) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(MOBILE_PANEL_EVENT, {
          detail: { panel }
        })
      );
    }
  }, []);

  const fetchUserData = async (token) => {
    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/users/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setUserData(data);
    } catch (error) {
      console.error('Fehler beim Abrufen der Benutzerdaten:', error);
    }
  };

  const openPanel = (e) => {
    e.preventDefault();
    emitPanelChange('account');
    setPanelOpen(true);
  };

  const closePanel = () => {
    setPanelOpen(false);
    emitPanelChange(null);
  };

  const goTo = (path) => {
    closePanel();
    router.push(path);
  };

  const handleLogout = () => {
    localStorage.removeItem('jwt');
    dispatch(userChangeIsLoggedIn(false));
    closePanel();
    router.push('/account/login');
  };

  const isLinkActive = (url) =>
    Boolean(pathname) && (pathname === url || pathname.startsWith(`${url}/`));

  const firstname = userData?.firstName || '';
  const lastname = userData?.lastName || '';
  const customerNumber = userData?.customerNumber || '';

  if (isLoggedIn || isTokenValid) {
    return (
      <>
        <Link
          href="/account/user-information"
          className="ps-block--user-account"
          style={{ zIndex: 2147483647, textDecoration: 'none' }}
          onClick={openPanel}
        >
          <div className="ps-block__header">
            <i className="icon-user" />
            <span className="account-text">
              Konto
            </span>
          </div>
        </Link>

        <div
          className={`aql-overlay ${panelOpen ? 'aql-overlay--open' : ''}`}
          onClick={closePanel}
        />
        <div className={`aql-panel ${panelOpen ? 'aql-panel--open' : ''}`}>
          <div className="aql-panel__header">
            <h3>Mein Konto</h3>
            <span className="aql-panel__close" onClick={closePanel}>
              <i className="icon-cross" />
            </span>
          </div>

          <div className="aql-panel__content">
            <div className="aql-user-info">
              <div className="aql-user-details">
                <span className="aql-user-name">
                  {userData
                    ? (`${firstname} ${lastname}`.trim() || userData.email || 'Mein Konto')
                    : 'Lade...'}
                </span>
                {userData === null ? null : customerNumber ? (
                  <span className="aql-customer-id">Kundennummer: {customerNumber}</span>
                ) : (
                  <span className="aql-customer-id">
                    Kundennummer:{' '}
                    <span
                      className="aql-customer-id--unconfirmed-link"
                      onClick={() => goTo('/account/user-information')}
                    >
                      <strong className="aql-customer-id--unconfirmed">Bestätigung erforderlich</strong>
                    </span>
                  </span>
                )}
              </div>
            </div>

            <ul className="aql-links">
              {accountNavLinks.map((item) => (
                <li
                  key={item.href}
                  className={isLinkActive(item.href) ? 'aql-links__active' : ''}
                  onClick={() => goTo(item.href)}
                >
                  <AccountLinkIcon icon={item.icon} />
                  <span>{item.label}</span>
                </li>
              ))}
              <li className="aql-links__logout" onClick={handleLogout}>
                <i
                  className="icon-power-switch"
                  style={{
                    fontSize: `${NAV_ICON_SIZE}px`,
                    width: `${NAV_ICON_SIZE}px`,
                    height: `${NAV_ICON_SIZE}px`,
                    lineHeight: '1',
                    marginRight: '14px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                />
                <span>Ausloggen</span>
              </li>
            </ul>
          </div>
        </div>

        <style jsx>{`
          :global(ol.carousel-indicators),
          :global(.carousel-indicators),
          :global(ol.carousel-indicators *),
          :global(.carousel-indicators *) {
            z-index: 1 !important;
            position: relative !important;
          }

          .ps-block--user-account {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-top: 12px;
            cursor: pointer;
            padding: 8px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .ps-block--user-account:hover {
            background: #f8f9fa;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }

          .ps-block__header {
            display: flex;
            flex-direction: column;
            align-items: center;
          }

          .ps-block__header i {
            font-size: 24px;
            color: #6c757d;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background: #f8f9fa;
            border: 2px solid #e9ecef;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }

          .account-text {
            margin-top: 6px;
            font-weight: 600;
            color: #333;
            font-size: 13px;
            letter-spacing: 0.3px;
            text-align: center;
          }

          .aql-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.45);
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.25s ease;
            z-index: 2147483646;
          }

          .aql-overlay--open {
            opacity: 1;
            visibility: visible;
          }

          .aql-panel {
            position: fixed;
            top: 0;
            left: 0;
            height: 100vh;
            width: 75%;
            background: #fff;
            transform: translateX(-100%);
            transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
            z-index: 2147483647;
            display: flex;
            flex-direction: column;
            box-shadow: 4px 0 16px rgba(0, 0, 0, 0.15);
            border-radius: 0 16px 16px 0;
            overflow: hidden;
          }

          .aql-panel--open {
            transform: translateX(0);
          }

          .aql-panel__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 16px;
            border-bottom: 1px solid ${COLOR_RED_DARK_BORDER};
            position: sticky;
            top: 0;
            z-index: 2;
            background: ${COLOR_RED_DARK};
          }

          .aql-panel__header h3 {
            margin: 0;
            font-size: 18px;
            text-transform: none;
            color: #fff;
          }

          .aql-panel__close {
            cursor: pointer;
            font-size: 18px;
            color: #fff;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 36px;
            height: 36px;
          }

          .aql-panel__content {
            padding: 0 16px 16px 16px;
            overflow-y: auto;
            overscroll-behavior: contain;
          }

          .aql-user-info {
            display: flex;
            align-items: center;
            padding: 18px 0;
            border-bottom: 1px solid #e0e0e0;
            margin-bottom: 6px;
          }

          .aql-user-details {
            flex: 1;
            min-width: 0;
          }

          .aql-user-name {
            display: block;
            font-weight: 600;
            color: #333;
            font-size: 17px;
            margin-bottom: 4px;
          }

          .aql-customer-id {
            display: flex;
            flex-direction: column;
            font-size: 14px;
            color: #6c757d;
          }

          .aql-customer-id--unconfirmed-link {
            cursor: pointer;
            text-decoration: none;
          }

          .aql-customer-id--unconfirmed-link:hover .aql-customer-id--unconfirmed {
            text-decoration: underline;
          }

          .aql-customer-id--unconfirmed {
            display: block;
            color: #bf0000;
            font-weight: 600;
            font-size: 13px;
            margin-top: 2px;
          }

          .aql-links {
            list-style: none;
            padding: 0;
            margin: 0;
          }

          .aql-links li {
            display: flex;
            align-items: center;
            padding: 14px 10px;
            border-bottom: 1px solid #e0e0e0;
            border-radius: 4px;
            cursor: pointer;
            transition: background-color 0.2s ease;
          }

          .aql-links li:hover {
            background-color: #f5f5f5;
          }

          .aql-links li span {
            font-size: 17px;
            font-weight: 500;
            color: #333;
          }

          /* Icons stammen aus der Unterkomponente AccountLinkIcon -> :global */
          .aql-links :global(i) {
            color: #6c757d;
          }

          .aql-links :global(i svg) {
            color: inherit;
          }

          .aql-links__active {
            background-color: #fdecec;
          }

          .aql-links__active span {
            color: ${COLOR_RED_DARK};
            font-weight: 700;
          }

          .aql-links__active :global(i) {
            color: ${COLOR_RED_DARK};
          }

          .aql-links__logout span {
            color: #c0392b;
          }

          .aql-links__logout :global(i) {
            color: #c0392b;
          }
        `}</style>
      </>
    );
  }

  return (
    <div className="ps-block--user-header" style={{ zIndex: 100 }}>
      <div className="ps-block__left">
        <i className="icon-user" />
      </div>
      <div className="ps-block__right">
        <Link href="/account/login">Anmelden</Link>
        <Link href="/account/register">Registrieren</Link>
      </div>
    </div>
  );
};

export default AccountQuickLinksMobile;